/**
 * LiveMonitorPanel.jsx
 *
 * Draggable dashboard panel that shows real-time SPY signal state from the alert engine.
 * Glows green/red when alerting, shows bull/bear arrow, confluence score, and
 * supplemental signal cards (VWAP, VIX, IV rank proxy, P/C ratio notes).
 *
 * Props:
 *   engineOn      — boolean
 *   muted         — boolean
 *   liveState     — engine state object | null
 *   alertCount    — number
 *   onStart       — fn
 *   onGoToAlerts  — fn (navigate to Alerts tab)
 *   vixQuote      — quotesBySym['^VIX'] from App
 */

import { useState } from 'react';

// ── Direction arrow ───────────────────────────────────────────────────────────

function DirectionArrow({ direction, score, alerting }) {
  const isBull    = direction === 'bullish';
  const isNeutral = direction === 'neutral';
  const color     = isBull ? 'var(--up)' : isNeutral ? 'var(--warn)' : 'var(--down)';
  const size      = 64;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{
        position: 'relative', width: size, height: size,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* Glow ring when alert active */}
        {alerting && !isNeutral && (
          <div style={{
            position: 'absolute', inset: -8, borderRadius: '50%',
            border: `2px solid ${color}`,
            boxShadow: `0 0 14px ${color}, 0 0 28px ${color}40`,
            animation: 'lmPulse 1.5s ease-in-out infinite',
          }} />
        )}
        <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: alerting && !isNeutral ? `drop-shadow(0 0 6px ${color})` : 'none' }}>
          {isNeutral ? (
            // Horizontal double arrow for neutral
            <g fill={color} opacity={0.75}>
              <polygon points="10,28 22,20 22,27 42,27 42,20 54,28 42,36 42,29 22,29 22,36" />
            </g>
          ) : isBull ? (
            // Bold up arrow
            <g fill={color}>
              <polygon points="32,6 56,38 42,38 42,58 22,58 22,38 8,38" />
            </g>
          ) : (
            // Bold down arrow
            <g fill={color}>
              <polygon points="32,58 8,26 22,26 22,6 42,6 42,26 56,26" />
            </g>
          )}
        </svg>
      </div>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase',
        color, opacity: isNeutral ? 0.6 : 1,
      }}>
        {isNeutral ? 'Neutral' : isBull ? 'Bullish' : 'Bearish'}
      </div>
      <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--faint)' }}>
        score {score >= 0 ? '+' : ''}{Math.round(score)}
      </div>
    </div>
  );
}

// ── Supplemental signal card ──────────────────────────────────────────────────

function SuppCard({ label, value, sub, color, warn, na }) {
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 5, flex: 1, minWidth: 80,
      border: `1px solid ${warn ? 'var(--warn)' : na ? 'var(--border)' : color ? color : 'var(--border)'}`,
      background: warn ? 'rgba(234,179,8,0.06)' : 'var(--bg)',
    }}>
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--faint)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: na ? 'var(--faint)' : warn ? 'var(--warn)' : color || 'var(--text)' }}>
        {na ? 'N/A' : value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: 9, color: warn ? 'var(--warn)' : 'var(--faint)', marginTop: 2, lineHeight: 1.3 }}>{sub}</div>}
    </div>
  );
}

// ── Factor badges ─────────────────────────────────────────────────────────────

function FactorBadge({ label, bull, warn }) {
  const col = warn ? 'var(--warn)' : bull ? 'var(--up)' : 'var(--down)';
  const bg  = warn ? 'rgba(234,179,8,0.1)' : bull ? 'var(--up-soft)' : 'var(--down-soft)';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 3, marginRight: 4, marginBottom: 4,
      fontSize: 9, fontWeight: 600, letterSpacing: '.05em',
      color: col, background: bg, border: `1px solid ${col}`,
    }}>
      {bull && !warn ? '▲ ' : !bull && !warn ? '▼ ' : '⚠ '}{label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LiveMonitorPanel({ engineOn, muted, liveState, alertCount, onStart, onGoToAlerts, vixQuote }) {
  const [showDetail, setShowDetail] = useState(false);

  const noData      = !engineOn || !liveState;
  const dir         = liveState?.confluence?.direction ?? 'neutral';
  const score       = liveState?.confluence?.score      ?? 0;
  const factors     = liveState?.confluence?.factors    ?? [];
  const supp        = liveState?.supplemental ?? {};
  const rsi         = liveState?.rsi;
  const price       = liveState?.price;
  const bb          = liveState?.bb;
  const vwap        = liveState?.vwap;
  const vwapDev     = liveState?.vwapDev;

  // Active alert: any persist counter has crossed threshold recently
  const persist     = liveState?.persist ?? {};
  const alerting    = engineOn && Object.values(persist).some(v => v >= 3);

  // VIX data: prefer engine-emitted, fall back to quotesBySym prop
  const vixPrice    = supp.vixPrice ?? vixQuote?.regularMarketPrice;
  const vixPct      = supp.vixPct   ?? vixQuote?.regularMarketChangePercent;
  const vixSpike    = supp.vixSpike ?? (vixPct != null && Math.abs(vixPct) >= 3);
  const ivRank      = supp.ivRankProxy;

  // Panel border glow colour
  const glowColor   = !engineOn ? null : alerting ? (dir === 'bullish' ? 'var(--up)' : 'var(--down)') : 'var(--accent)';
  const glowStyle   = glowColor
    ? { boxShadow: `0 0 0 1px ${glowColor}, 0 0 12px ${glowColor}40` }
    : {};

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', gap: 8,
      padding: '10px 12px', boxSizing: 'border-box',
      ...glowStyle, borderRadius: 6, transition: 'box-shadow .4s',
    }}>

      {/* ── Header row ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--faint)', fontWeight: 700 }}>
          SPY Signal
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {engineOn && alertCount > 0 && (
            <span style={{ fontSize: 9, fontFamily: 'monospace', color: 'var(--warn)', fontWeight: 700 }}>
              {alertCount} alert{alertCount !== 1 ? 's' : ''}
            </span>
          )}
          {engineOn && muted && (
            <span style={{ fontSize: 9, color: 'var(--warn)', fontWeight: 700, letterSpacing: '.06em' }}>MUTED</span>
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            {!engineOn && (
              <button onClick={onStart} style={{
                fontSize: 9, padding: '2px 8px', borderRadius: 3, cursor: 'pointer', fontWeight: 700,
                border: '1px solid var(--up)', color: 'var(--up)', background: 'var(--up-soft)', letterSpacing: '.06em',
              }}>▶ Start</button>
            )}
            <button onClick={onGoToAlerts} style={{
              fontSize: 9, padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
              border: '1px solid var(--border)', color: 'var(--faint)', background: 'none', letterSpacing: '.06em',
            }}>Settings ↗</button>
          </div>
        </div>
      </div>

      {/* ── No data state ────────────────────────────────────────────────── */}
      {noData ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--faint)', textAlign: 'center', lineHeight: 1.6 }}>
            {!engineOn ? 'Start the alert engine to monitor signals' : 'Polling… first bar loads within 60s.'}
          </div>
          {!engineOn && (
            <button onClick={onStart} style={{
              fontSize: 11, padding: '5px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 700,
              border: '1px solid var(--up)', color: 'var(--up)', background: 'var(--up-soft)',
            }}>▶ Start engine</button>
          )}
        </div>
      ) : (
        <>
          {/* ── Main: arrow + core numbers ─────────────────────────────── */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <DirectionArrow direction={dir} score={score} alerting={alerting} />

            {/* Core readings */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* RSI bar */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 2, color: 'var(--muted)' }}>
                  <span>RSI (14)</span>
                  <span style={{
                    fontFamily: 'monospace', fontWeight: 700,
                    color: rsi != null && rsi < 30 ? 'var(--up)' : rsi != null && rsi > 70 ? 'var(--down)' : 'var(--text)',
                  }}>{rsi?.toFixed(1) ?? '—'}</span>
                </div>
                <div style={{ height: 5, background: 'var(--bg)', borderRadius: 3, border: '1px solid var(--border)', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '30%', width: 1, height: '100%', background: 'var(--up)', opacity: 0.4 }} />
                  <div style={{ position: 'absolute', left: '70%', width: 1, height: '100%', background: 'var(--down)', opacity: 0.4 }} />
                  {rsi != null && (
                    <div style={{ width: `${rsi}%`, height: '100%', borderRadius: 3, transition: 'width .5s, background .3s',
                      background: rsi < 30 ? 'var(--up)' : rsi > 70 ? 'var(--down)' : 'var(--border)' }} />
                  )}
                </div>
              </div>

              {/* Price vs BB */}
              {bb && price != null && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 2, color: 'var(--muted)' }}>
                    <span>Price vs BB</span>
                    <span style={{
                      fontFamily: 'monospace', fontWeight: 700,
                      color: price < bb.lower ? 'var(--up)' : price > bb.upper ? 'var(--down)' : 'var(--text)',
                    }}>${price.toFixed(2)}</span>
                  </div>
                  <div style={{ height: 5, background: 'var(--bg)', borderRadius: 3, border: '1px solid var(--border)', overflow: 'hidden', position: 'relative' }}>
                    {(() => {
                      const range = bb.upper - bb.lower + bb.std * 2;
                      const low   = bb.lower - bb.std;
                      const pct   = Math.max(0, Math.min(100, ((price - low) / range) * 100));
                      const loPct = (bb.std / range) * 100;
                      const hiPct = 100 - loPct;
                      return <>
                        <div style={{ position: 'absolute', left: `${loPct}%`, width: 1, height: '100%', background: 'var(--up)', opacity: 0.4 }} />
                        <div style={{ position: 'absolute', left: `${hiPct}%`, width: 1, height: '100%', background: 'var(--down)', opacity: 0.4 }} />
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, transition: 'width .5s, background .3s',
                          background: price < bb.lower ? 'var(--up)' : price > bb.upper ? 'var(--down)' : 'var(--border)' }} />
                      </>;
                    })()}
                  </div>
                </div>
              )}

              {/* VWAP deviation */}
              {vwapDev != null && (
                <div style={{ fontSize: 10, color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>vs VWAP</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: vwapDev < -0.3 ? 'var(--up)' : vwapDev > 0.3 ? 'var(--down)' : 'var(--text)' }}>
                    {vwapDev >= 0 ? '+' : ''}{vwapDev.toFixed(3)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Active confluence factors ───────────────────────────────── */}
          {factors.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
              {factors.map((f, i) => <FactorBadge key={i} label={f.label} bull={f.bull} warn={f.warn} />)}
            </div>
          )}

          {/* ── Supplemental signal cards ───────────────────────────────── */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {/* VIX */}
            <SuppCard
              label="VIX"
              value={vixPrice != null ? vixPrice.toFixed(2) : null}
              sub={vixPct != null ? `${vixPct >= 0 ? '+' : ''}${vixPct.toFixed(1)}% today${vixSpike ? ' ⚠ spike' : ''}` : null}
              warn={vixSpike}
              color={vixPct != null ? (vixPct > 0 ? 'var(--down)' : 'var(--up)') : null}
            />
            {/* IV Rank proxy */}
            <SuppCard
              label="IV Rank (proxy)"
              value={ivRank ? `~${ivRank.rank}` : null}
              sub={ivRank ? ivRank.note : null}
              color={ivRank ? (ivRank.rank < 30 ? 'var(--down)' : ivRank.rank > 70 ? 'var(--up)' : 'var(--muted)') : null}
            />
            {/* VWAP confluence */}
            <SuppCard
              label="VWAP conf."
              value={vwapDev != null ? `${Math.abs(vwapDev).toFixed(2)}%` : null}
              sub={vwapDev != null
                ? vwapDev < -0.3 ? 'Below — bull confluence'
                : vwapDev >  0.3 ? 'Above — bear confluence'
                : 'Near VWAP'
                : null}
              color={vwapDev != null ? (vwapDev < -0.3 ? 'var(--up)' : vwapDev > 0.3 ? 'var(--down)' : null) : null}
            />
            {/* Put/Call ratio */}
            <SuppCard
              label="P/C Ratio"
              value={null}
              sub="CBOE feed needed"
              na={true}
            />
          </div>

          {/* ── Toggle detail ───────────────────────────────────────────── */}
          <button onClick={() => setShowDetail(d => !d)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 10, color: 'var(--faint)', textAlign: 'left', padding: 0,
          }}>
            {showDetail ? '▲ hide' : '▼ detail'}
          </button>

          {showDetail && (
            <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--faint)', lineHeight: 1.8, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
              {rsi != null && <div>RSI {rsi.toFixed(1)} · {rsi < 30 ? 'oversold → bullish bias' : rsi > 70 ? 'overbought → bearish bias' : 'neutral zone'}</div>}
              {bb && price != null && <div>BB lower ${bb.lower.toFixed(2)} · mid ${bb.mid.toFixed(2)} · upper ${bb.upper.toFixed(2)}</div>}
              {vwap != null && <div>VWAP ${vwap.toFixed(2)} · price ${price?.toFixed(2)}</div>}
              {vixPrice != null && <div>VIX {vixPrice.toFixed(2)} · IV rank proxy ~{ivRank?.rank ?? '?'} ({ivRank?.label ?? ''})</div>}
              <div style={{ marginTop: 4, color: 'var(--faint)', opacity: 0.7 }}>
                P/C ratio: needs CBOE OPRA feed or paid data provider (e.g. Market Chameleon, Tradier).
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes lmPulse { 0%,100%{opacity:.6} 50%{opacity:1} }
      `}</style>
    </div>
  );
}
